import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { isNil } from '@trimble-gcs/common';
import { ClassifierType, DEFAULT_CLASSIFIER_TYPE } from '../classifier/classifier.model';
import {
  AddToImportScans,
  PatchImportFile,
  PatchImportScan,
  ResetImportScans,
  SetClassifierType,
  SetCombineToSingleScan,
  SetRunClassifier,
} from './import.actions';
import { ImportFileStatus, ImportScan, ImportScanStatus, ImportStatus } from './import.models';

export interface ImportStateModel {
  importScans: ImportScan[];
  combineToSingleScan: boolean;
  runClassifier: boolean;
  classifierType: ClassifierType;
}

const defaultModel: ImportStateModel = {
  importScans: [],
  combineToSingleScan: true,
  runClassifier: false,
  classifierType: DEFAULT_CLASSIFIER_TYPE,
};

@State<ImportStateModel>({
  name: 'importState',
  defaults: defaultModel,
})
@Injectable()
export class ImportState {
  @Selector() static importScans(state: ImportStateModel): ImportScan[] {
    return state.importScans;
  }

  @Selector() static status(state: ImportStateModel): ImportStatus {
    const importScans = state.importScans;
    if (importScans.length === 0) return ImportStatus.NotStarted;

    const busy = importScans.some(
      (scan) =>
        scan.status !== ImportScanStatus.Error && scan.status !== ImportScanStatus.Completed,
    );
    if (busy) return ImportStatus.Busy;

    const error = importScans.some((scan) => scan.status === ImportScanStatus.Error);
    if (error) return ImportStatus.Error;

    return ImportStatus.Completed;
  }

  @Selector() static activeImportFileCount(state: ImportStateModel): number {
    const activeImports = state.importScans.reduce((prev, cur) => {
      if (cur.status === ImportScanStatus.Completed || cur.status === ImportScanStatus.Error) {
        return prev;
      }

      const fileCount = cur.files.filter(
        (file) => file.status === ImportFileStatus.Pending || file.status === ImportFileStatus.Busy,
      ).length;

      return fileCount > 0 ? prev + fileCount : prev + 1;
    }, 0);

    return activeImports;
  }

  @Selector() static combineToSingleScan(state: ImportStateModel) {
    return state.combineToSingleScan;
  }

  @Selector() static runClassifier(state: ImportStateModel) {
    return state.runClassifier;
  }

  @Selector() static classifierType(state: ImportStateModel) {
    return state.classifierType;
  }

  @Action(SetCombineToSingleScan)
  setCombineToSingleScan(
    ctx: StateContext<ImportStateModel>,
    { combineToSingleScan }: SetCombineToSingleScan,
  ) {
    ctx.patchState({ combineToSingleScan });
  }

  @Action(SetRunClassifier)
  setRunClassifier(ctx: StateContext<ImportStateModel>, { runClassifier }: SetRunClassifier) {
    ctx.patchState({ runClassifier });
  }

  @Action(SetClassifierType)
  setClassifierType(ctx: StateContext<ImportStateModel>, { classifierType }: SetClassifierType) {
    ctx.patchState({ classifierType });
  }

  @Action(AddToImportScans)
  addToImportScans(ctx: StateContext<ImportStateModel>, { importScans }: AddToImportScans) {
    const combined = [...ctx.getState().importScans, ...importScans];
    ctx.patchState({ importScans: combined });
  }

  @Action(PatchImportScan)
  patchImportScan(ctx: StateContext<ImportStateModel>, { importScan }: PatchImportScan) {
    const importScans = ctx.getState().importScans.map((item) => {
      return item.id === importScan.id ? importScan : item;
    });
    ctx.patchState({ importScans });
  }

  @Action(PatchImportFile)
  patchImportFile(ctx: StateContext<ImportStateModel>, { importFile }: PatchImportFile) {
    const importScans = ctx.getState().importScans.map((scan) => {
      const foundFile = scan.files.some((file) => file.id === importFile.id);
      if (isNil(foundFile)) return scan;

      return {
        ...scan,
        files: scan.files.map((file) => (file.id === importFile.id ? importFile : file)),
      };
    });

    ctx.patchState({ importScans });
  }

  @Action(ResetImportScans)
  resetImportScans(ctx: StateContext<ImportStateModel>) {
    ctx.setState(defaultModel);
  }
}
